/**
 * 月子中心派单内容
 * 左右分栏：左侧客人列表，右侧套餐详情 + 房间信息 + 套餐项目列表
 */
import React from 'react'
import {
  Layout,
  List,
  Table,
  Tag,
  Button,
  Empty,
  Spin,
  Card,
  Tabs,
  Descriptions,
  message,
  Typography,
  Badge,
  Drawer
} from 'antd'
import { WarningOutlined, CalendarOutlined, LeftOutlined } from '@ant-design/icons'
import { SelectC, Search } from '@/i2antdV5'
import dayjs from 'dayjs'
import {
  renderTextStr,
  formatDateTimeStr,
  formatDayString,
  renderRightMoney,
  resolveOrderTotalIdFromUnassignedPlan,
  renderCountOrGrayZero
} from '@/utils/renderUtils'
import {
  fetchMaternityCenterOrderProfile,
  fetchMaternityCenterChanKangProjects,
  fetchMaternityCenterOrderProjectArray
} from '@/redux/order/maternityCenterOrderSlice'
import { setSelectedOrderId, clearSelectedOrder } from '@/redux/order/dispatchPageSlice'
import SingleProjectAppointmentModal from '../share/SingleProjectAppointmentModal'
import CancelMemberProjectPlansModal from '../share/CancelMemberProjectPlansModal'
import Permission from '@/components/PermissionA5'

import DispatcherTechnicianModal from '../share/DispatcherTechnicianModal'
import { getProjectPlanUnassignedCount } from '@/service/dispatchPage'
import UnassignedProjectPlanBookPanel from '../share/UnassignedProjectPlanBookPanel'

/** 与 AppHeader 手机导航一致：窄屏派单页改为上下布局 */
const MOBILE_LAYOUT_MAX_WIDTH = 768

const { Sider, Content } = Layout
const { Text } = Typography

// 左栏 Tab：id 与 dispatchOrderList.query.checkInStatus 一致；「新订单」角标用 GET /project-plan/unassigned/count
const CHECK_IN_STATUS_OPTIONS = [
  { id: 2, key: 'checked_in', value: '温馨入住中' },
  { id: 1, key: 'new_orders', value: '新订单' }
]

/** 激活状态下：新订单角标数量 + 新订单列表同一定时器同步轮询 30 秒*/
const UNASSIGNED_SYNC_POLL_MS = 30_000

/** 角标用：Java 包 data 可能为 number 或 { count: number } */
function parseProjectPlanUnassignedCount(res) {
  const d = res?.data
  if (typeof d === 'number' && !Number.isNaN(d)) return Math.max(0, Math.floor(d))
  if (d && typeof d === 'object' && typeof d.count === 'number' && !Number.isNaN(d.count)) {
    return Math.max(0, Math.floor(d.count))
  }
  return 0
}

/** ProjectPlanVO 时间：Unix 秒，≤10 位按秒处理。列表/详情中时段起止用 formatUnassignedHmSec 只显示时:分 */
function formatUnassignedHmSec(t) {
  if (t == null || t === '') return '—'
  const n = Number(t)
  if (!Number.isFinite(n)) return '—'
  const d = String(Math.abs(n)).length <= 10 ? dayjs.unix(n) : dayjs(n)
  return d.isValid() ? d.format('HH:mm') : '—'
}

function formatUnassignedDaySec(t) {
  if (t == null || t === '') return '—'
  const n = Number(t)
  if (!Number.isFinite(n)) return '—'
  const d = String(Math.abs(n)).length <= 10 ? dayjs.unix(n) : dayjs(n)
  return d.isValid() ? d.format('YYYY-MM-DD') : '—'
}

// 套餐项目执行状态（API 字段 executeStatus 的枚举值）
const ITEM_EXEC_STATUS = {
  WAIT_APPOINTMENT: { text: '待预约', color: 'warning' },
  WAIT_EXECUTE: { text: '待执行', color: 'warning' },
  EXECUTING: { text: '执行中', color: 'processing' },
  COMPLETED: { text: '已完成', color: 'success' }
}

// 产康项目状态筛选选项
const STATUS_FILTER_OPTIONS = [
  { key: 'all', label: '全部', color: 'processing' },
  { key: 'WAIT_APPOINTMENT', label: '待预约', color: 'warning' },
  { key: 'WAIT_EXECUTE', label: '待执行', color: 'warning' },
  { key: 'EXECUTING', label: '执行中', color: 'processing' },
  { key: 'COMPLETED', label: '已完成', color: 'success' }
]

/**
 * roomPlans 的 stime/etime：与 formatDayString 一致，数值位数 ≤10 视为秒级 Unix 时间戳
 */
function dayjsFromRoomPlanTime(t) {
  if (t == null || t === '') return dayjs(null)
  if (typeof t === 'number') {
    return t.toString().length <= 10 ? dayjs.unix(t) : dayjs(t)
  }
  return dayjs(t)
}

/**
 * 单条房间预约相对「今天」的时段（按自然日比较）
 * @returns {'past'|'current'|'future'|'unknown'}
 */
function classifyRoomPeriod(r) {
  if (!r || !r.stime || !r.etime) return 'unknown'
  const start = dayjsFromRoomPlanTime(r.stime)
  const end = dayjsFromRoomPlanTime(r.etime)
  if (!start.isValid() || !end.isValid()) return 'unknown'
  if (end.startOf('day').isBefore(start.startOf('day'))) return 'unknown'
  const today = dayjs().startOf('day')
  const endDay = end.startOf('day')
  const startDay = start.startOf('day')
  if (endDay.isBefore(today)) return 'past'
  if (startDay.isAfter(today)) return 'future'
  return 'current'
}

function partitionRoomsByPeriod(roomPlans) {
  const arr = Array.isArray(roomPlans) ? roomPlans : []
  const enriched = arr.map((r, i) => ({
    ...r,
    _key: `room-${i}-${String(r.stime)}-${String(r.etime)}`,
    period: classifyRoomPeriod(r)
  }))
  const sortByStart = (a, b) => {
    const ta = a.stime ? dayjsFromRoomPlanTime(a.stime).valueOf() : 0
    const tb = b.stime ? dayjsFromRoomPlanTime(b.stime).valueOf() : 0
    return ta - tb
  }
  return {
    current: enriched.filter((x) => x.period === 'current').sort(sortByStart),
    future: enriched.filter((x) => x.period === 'future').sort(sortByStart),
    past: enriched.filter((x) => x.period === 'past').sort(sortByStart),
    unknown: enriched.filter((x) => x.period === 'unknown').sort(sortByStart)
  }
}

/** 入住中 → 待入住 → 已结束 → 日期不全（区内按开始日升序） */
function flattenRoomsOrderedByPeriod(roomPlans) {
  const { current, future, past, unknown } = partitionRoomsByPeriod(roomPlans)
  return [...current, ...future, ...past, ...unknown]
}

const ROOM_PERIOD_TAG = {
  current: { color: 'success', label: '入住中' },
  future: { color: 'processing', label: '待入住' },
  past: { color: 'default', label: '已结束' },
  unknown: { color: 'warning', label: '不全' }
}

// 套餐项目 type1/2/3 表格列（与权限拆分对应，列定义只维护一份）
const ORDER_PROJECT_TABLE_COLUMNS = [
  { title: '序号', key: 'index', width: 56, align: 'center', render: (_, __, index) => index + 1 },
  { title: '项目名称', dataIndex: 'projectName', key: 'projectName', render: (t) => renderTextStr(t) },
  { title: '项目类型', dataIndex: 'typeText', key: 'typeName', render: (t) => renderTextStr(t) },
  { title: '购买/可用', key: 'numUseNum', render: (_, r) => `${renderTextStr(r.num)} / ${renderTextStr(r.useNum)}` }
]

class MaternityCenterDispatcherContent extends React.Component {
  constructor(props) {
    super(props)
    const embedNewOrdersMobile = props.embedMode === 'newOrdersMobile'
    this.embedNewOrdersMobile = embedNewOrdersMobile
    this.state = {
      siderCollapsed: false,
      headerReady: true,
      projectNameKeyword: '',
      execStatusFilter: 'all',
      projectPlanUnassignedCount: 0,
      /** 新订单列表 Tab：选中的项目安排 id（非 order-total 订单 id） */
      selectedUnassignedPlanId: null,
      isMobileLayout: embedNewOrdersMobile
        ? true
        : (typeof window !== 'undefined' &&
            window.matchMedia(`(max-width: ${MOBILE_LAYOUT_MAX_WIDTH}px)`).matches)
    }
    this.expandTimerRef = null
    this.mobileLayoutMqRef = null
    this.unassignedSyncPollRef = null
    /** 静默列表轮询不置 Redux fetching，用 ref 防并发；否则 30s 内可能叠两次请求 */
    this.unassignedSilentListPollInFlight = false
    /** 避免 visibilitychange(visible) 与首次 mount 的 tick 连续打两次 count */
    this._prevDocHiddenForPoll =
      typeof document !== 'undefined' && document.visibilityState === 'hidden'
    this.handleMobileLayoutMqChange = this.handleMobileLayoutMqChange.bind(this)
  }

  componentDidMount() {
    if (!this.embedNewOrdersMobile) {
      this.mobileLayoutMqRef = window.matchMedia(`(max-width: ${MOBILE_LAYOUT_MAX_WIDTH}px)`)
      this.mobileLayoutMqRef.addEventListener('change', this.handleMobileLayoutMqChange)
    }
    document.addEventListener('visibilitychange', this.handlePageVisibilityForUnassignedPoll)
    this.runUnassignedSyncPollTick()
    this.syncUnassignedSyncPolling()
  }

  componentWillUnmount() {
    if (this.expandTimerRef) clearTimeout(this.expandTimerRef)
    this.stopUnassignedSyncPolling()
    document.removeEventListener('visibilitychange', this.handlePageVisibilityForUnassignedPoll)
    if (this.mobileLayoutMqRef) {
      this.mobileLayoutMqRef.removeEventListener('change', this.handleMobileLayoutMqChange)
    }
  }

  handleMobileLayoutMqChange() {
    if (this.embedNewOrdersMobile) return
    if (!this.mobileLayoutMqRef) return
    const next = this.mobileLayoutMqRef.matches
    this.setState((prev) => {
      if (prev.isMobileLayout === next) return null
      const patch = { isMobileLayout: next }
      if (next) {
        patch.siderCollapsed = false
        patch.headerReady = true
      }
      return patch
    })
  }

  loadProjectPlanUnassignedCount = () => {
    const query = this.props.orderObj?.dispatchOrderList?.query || {}
    const apiParams = {}
    if (query.storeId != null && String(query.storeId).trim() !== '') {
      apiParams.storeId = query.storeId
    }
    getProjectPlanUnassignedCount(apiParams)
      .then((res) => {
        const n = parseProjectPlanUnassignedCount(res)
        this.setState((prev) =>
          prev.projectPlanUnassignedCount === n ? null : { projectPlanUnassignedCount: n }
        )
      })
      .catch(() => {})
  }

  /**
   * 激活状态下同一节拍：
   * - 新订单 Tab：只触发列表请求；列表成功后再刷 count（有 loading 的首次请求在 componentDidUpdate 里带一次 count；
   *   定时静默拉列表在请求结束后自行刷 count，且 silent 不置 fetching，避免左侧 Spin + 全页重绘打断右侧排期等操作）
   * - 其他 Tab：列表不需要轮询，此时单独刷 count，保持「新订单」Tab 角标同步。
   */
  runUnassignedSyncPollTick = () => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
    const { fetchOrders, orderObj } = this.props
    const query = orderObj?.dispatchOrderList?.query
    const isUnassignedTab = String(query?.checkInStatus ?? '') === '1'
    if (isUnassignedTab) {
      if (this.unassignedSilentListPollInFlight) return
      if (orderObj?.dispatchOrderList?.fetching) return
      if (typeof fetchOrders !== 'function') return
      this.unassignedSilentListPollInFlight = true
      Promise.resolve(fetchOrders(query, { silent: true }))
        .then((action) => {
          if (action && action.error) return
          if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
          this.loadProjectPlanUnassignedCount()
        })
        .finally(() => {
          this.unassignedSilentListPollInFlight = false
        })
      return
    }
    this.loadProjectPlanUnassignedCount()
  }

  stopUnassignedSyncPolling = () => {
    if (this.unassignedSyncPollRef != null) {
      clearInterval(this.unassignedSyncPollRef)
      this.unassignedSyncPollRef = null
    }
  }

  /**
   * 前台只保留一支 interval。先 clear 再创建，避免隐藏时未卸掉的定时器与回到前台新挂的定时器叠成双份 count 轮询。
   */
  startUnassignedSyncPolling = () => {
    this.stopUnassignedSyncPolling()
    const docVisible = typeof document === 'undefined' || document.visibilityState === 'visible'
    if (!docVisible) return
    this.unassignedSyncPollRef = window.setInterval(
      () => this.runUnassignedSyncPollTick(),
      UNASSIGNED_SYNC_POLL_MS
    )
  }

  /** 仅浏览器页签在前台时轮询；后台卸掉定时器，避免后台仍跑一支、激活后再挂一支 */
  syncUnassignedSyncPolling = () => {
    const docVisible = typeof document === 'undefined' || document.visibilityState === 'visible'
    if (docVisible) {
      this.startUnassignedSyncPolling()
    } else {
      this.stopUnassignedSyncPolling()
    }
  }

  handlePageVisibilityForUnassignedPoll = () => {
    if (typeof document === 'undefined') return
    const hidden = document.visibilityState === 'hidden'
    // 仅「从后台回到前台」立即同步一轮；初始进入页面已在 mount 里 tick，避免重复请求 count
    if (!hidden && this._prevDocHiddenForPoll) {
      this.runUnassignedSyncPollTick()
    }
    this._prevDocHiddenForPoll = hidden
    this.syncUnassignedSyncPolling()
  }

  componentDidUpdate(prevProps) {
    const { dispatchPage, orderObj } = this.props
    const { selectedOrderId } = dispatchPage
    const { selectedOrderId: prevSelectedOrderId } = prevProps.dispatchPage
    const { dispatchOrderList: { query: q } = {} } = orderObj || {}
    const { dispatchOrderList: { query: prevQ } = {} } = prevProps.orderObj || {}
    if (String(q?.checkInStatus ?? '') !== String(prevQ?.checkInStatus ?? '')) {
      this.setState({
        selectedUnassignedPlanId: null,
        projectNameKeyword: '',
        execStatusFilter: 'all'
      })
    }
    if (String(q?.storeId ?? '') !== String(prevQ?.storeId ?? '')) {
      this.loadProjectPlanUnassignedCount()
    } else if (selectedOrderId !== prevSelectedOrderId) {
      this.setState({ projectNameKeyword: '', execStatusFilter: 'all' })
    }
    const wasFetching = prevProps.orderObj?.dispatchOrderList?.fetching
    const nowFetching = orderObj?.dispatchOrderList?.fetching
    const docVisible = typeof document === 'undefined' || document.visibilityState === 'visible'
    if (
      wasFetching &&
      !nowFetching &&
      String(q?.checkInStatus ?? '') === '1' &&
      docVisible
    ) {
      this.loadProjectPlanUnassignedCount()
    }
  }

  /** 将 technicians/nurses 数组格式化为展示字符串，取 item.name */
  formatPrincipalNames = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) return '--'
    return arr.map((x) => (typeof x === 'object' && x != null ? (x.name ?? '') : String(x))).filter(Boolean).join('、') || '--'
  }

  /** 与月子中心订单列表「服务时间」一致：开始优先实际入住 momCheckInTime，否则预约入住 momAppointInTime */
  formatMaternityServiceStartDay = (detail) => {
    if (!detail || typeof detail !== 'object') return '--'
    const raw =
      detail.momCheckInTime && detail.momCheckInTime !== 0
        ? detail.momCheckInTime
        : detail.momAppointInTime
    if (!raw) return '--'
    const s = formatDayString(raw)
    return s === '' ? '--' : s
  }

  /** 服务结束：离店/离院时间 leaveTime */
  formatMaternityServiceEndDay = (detail) => {
    if (!detail || typeof detail !== 'object') return '--'
    const raw = detail.leaveTime
    if (!raw) return '--'
    const s = formatDayString(raw)
    return s === '' ? '--' : s
  }

  /** 入住房间：内联流式排版（flex-wrap），多条可同排展示，不占满整行表格 */
  renderRoomBookingList = (roomPlans) => {
    const rows = flattenRoomsOrderedByPeriod(roomPlans)
    
    return (
      <div className="room-booking-inline" role="list" aria-label="房间预约列表">
        {rows.map((r) => {
          const cfg = ROOM_PERIOD_TAG[r.period] || ROOM_PERIOD_TAG.unknown
          const startStr = r.stime ? formatDayString(r.stime) : ''
          const endStr = r.etime ? formatDayString(r.etime) : ''
          const rangeShort =
            startStr && endStr ? `${startStr}~${endStr}` : startStr || endStr || '—'
          return (
            <span
              key={r._key}
              className={`room-booking-inline-chunk room-booking-inline-chunk--${r.period}`}
              role="listitem"
            >
              <Tag color={cfg.color} className="room-booking-tag-compact">
                {cfg.label}
              </Tag>
              <span className="room-booking-inline-name">{renderTextStr(r.name)}</span>
              <Text type="secondary" className="room-booking-inline-range">
                {rangeShort}
              </Text>
            </span>
          )
        })}
      </div>
    )
  }

  /** 套餐详情「服务起止与天数」区块：起止一行、天数一行，避免 `~ /` 挤在一起难读 */
  renderServicePeriodSummary = (detail) => {
    const start = this.formatMaternityServiceStartDay(detail)
    const end = this.formatMaternityServiceEndDay(detail)

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '0 6px' }}>
          <Text type="secondary" style={{ fontSize: 12, flexShrink: 0 }}>
            起止
          </Text>
          <span>{start}</span>
          <Text type="secondary" style={{ fontSize: 13, padding: '0 2px' }}>
            ~
          </Text>
          <span>{end}</span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '0 6px' }}>
          <Text type="secondary" style={{ fontSize: 12, flexShrink: 0 }}>
            套餐天数
          </Text>
          <span>{renderTextStr(detail?.totalServiceDay)}</span>
        </div>
      </div>
    )
  }

  /** 获取项目执行状态，优先使用 API 的 executeStatus，无则兼容旧逻辑 */
  getPackageItemExecStatus = (item) => {
    const apiStatus = item?.executeStatus
    if (apiStatus && ITEM_EXEC_STATUS[apiStatus]) return apiStatus
    const hasExecutor = !!item.actionUserName
    const hasExecTime = !!item.actionTime
    if (hasExecutor && hasExecTime) return 'COMPLETED'
    if (hasExecutor && !hasExecTime) return 'EXECUTING'
    return 'WAIT_APPOINTMENT'
  }

  buildPackageItemsWithRowSpan = (items) => {
    if (!Array.isArray(items) || items.length === 0) return []
    const sorted = [...items].sort((a, b) =>
      (a?.projectName ?? '').localeCompare(b?.projectName ?? '')
    )
    return sorted.map((r, i) => {
      const name = r?.projectName ?? ''
      const prevName = i > 0 ? sorted[i - 1]?.projectName ?? '' : null
      const isFirstOfGroup = name !== prevName
      const groupCount = isFirstOfGroup
        ? sorted.filter((x) => (x?.projectName ?? '') === name).length
        : 0
      return {
        ...r,
        _projectNameRowSpan: isFirstOfGroup ? groupCount : 0
      }
    })
  }

  /** 套餐项目分类表（单类）；typeIndex 用于行 key 与默认标题 */
  renderOrderProjectArrayCard = (list, typeIndex) => {
    if (!Array.isArray(list) || list.length === 0) return null
    const title = renderTextStr(list[0]?.projectTypeText) || `类型${typeIndex + 1}`
    return (
      <Card title={title} size="small" className="detail-card">
        <Table
          dataSource={list}
          rowKey={(r) => r.id ?? `${r.projectName}-${r.num}-${r.typeName}-${typeIndex}`}
          pagination={false}
          size="small"
          columns={ORDER_PROJECT_TABLE_COLUMNS}
        />
      </Card>
    )
  }

  handleSelectUnassignedPlan = (row) => {
    const { dispatch } = this.props
    if (!row || row.id == null) return
    dispatch(clearSelectedOrder())
    this.setState({ selectedUnassignedPlanId: row.id })
    const orderTotalId = resolveOrderTotalIdFromUnassignedPlan(row)
    if (orderTotalId != null && String(orderTotalId).trim() !== '') {
      dispatch(fetchMaternityCenterOrderProfile({ id: orderTotalId }))
    }
  }

  handleSelectOrder = (order) => {
    const { dispatch } = this.props
    if (!order || !order.id) return
    this.setState({ selectedUnassignedPlanId: null })
    dispatch(setSelectedOrderId(order.id))
    dispatch(fetchMaternityCenterOrderProfile({ id: order.id }))
    dispatch(fetchMaternityCenterChanKangProjects({ id: order.id }))
    dispatch(fetchMaternityCenterOrderProjectArray({ id: order.id, query: {} }))
  }

  /** modal 提交成功后统一刷新：列表 + 详情（order 或 orderId 均可） */
  handleModalSuccess = (orderOrOrderId) => {
    if (typeof this.props.refreshList === 'function') this.props.refreshList()
    const order = orderOrOrderId && typeof orderOrOrderId === 'object'
      ? orderOrOrderId
      : (orderOrOrderId != null ? { id: orderOrOrderId } : null)
    if (order) this.handleSelectOrder(order)
  }

  renderLeftSider = () => {
    const { isMobileLayout } = this.state
    const { orderObj, jStoreOptions, dispatchPage, fetchOrders, handleFilterChange } = this.props
    const { dispatchOrderList: orderListData } = orderObj
    const { fetching, data = {}, query } = orderListData
    const { records = [], totalRow = 0, pageNumber = 1, pageSize = 20 } = data
    const { selectedOrderId } = dispatchPage
    const checkInTab = query?.checkInStatus ?? 2
    const isUnassignedTab = String(checkInTab) === '1'
    const { selectedUnassignedPlanId } = this.state

    return (
      <Sider
        width={isMobileLayout ? '100%' : 320}
        collapsed={isMobileLayout ? false : this.state.siderCollapsed}
        collapsedWidth={isMobileLayout ? 0 : 68}
        theme="light"
        className={`dispatch-sider ${this.state.siderCollapsed ? 'collapsed' : ''}`}
        collapsible={!isMobileLayout}
        onCollapse={(collapsed) => {
          if (this.expandTimerRef) {
            clearTimeout(this.expandTimerRef)
            this.expandTimerRef = null
          }
          if (collapsed) {
            this.setState({ siderCollapsed: true, headerReady: false })
          } else {
            this.setState({ siderCollapsed: false })
            this.expandTimerRef = setTimeout(() => {
              this.expandTimerRef = null
              this.setState({ headerReady: true })
            }, 350)
          }
        }}
      >
        {!this.state.siderCollapsed && this.state.headerReady && (
          <div className="sider-header" key="sider-header">
            {this.embedNewOrdersMobile ? (
              <div className="sider-embed-title">
                {this.state.projectPlanUnassignedCount > 0 ? (
                  <Badge count={this.state.projectPlanUnassignedCount} size="small" offset={[8, 0]}>
                    <span>新订单</span>
                  </Badge>
                ) : (
                  '新订单'
                )}
              </div>
            ) : (
              <>
                <SelectC label="门店"
                  query={query}
                  searchKey="storeId"
                  options={(jStoreOptions || []).map((s) => ({ id: s.id, value: s.value }))}
                  action={handleFilterChange}
                  placeholder="请选择门店"
                  modalProps={{ style: { width: '100%' }, allowClear: false }}
                />
                <Tabs
                  activeKey={`${query?.checkInStatus ?? 2}`}
                  onChange={(key) => {
                    handleFilterChange({ ...query, checkInStatus: key })
                  }}
                  size="small"
                  style={{ width: '100%' }}
                  items={CHECK_IN_STATUS_OPTIONS.map((opt) => {
                    const isNewOrderTab = opt.id === 1
                    const c = this.state.projectPlanUnassignedCount
                    const label =
                      isNewOrderTab && c > 0 ? (
                        <Badge count={c} size="small" offset={[6, 0]}>
                          <span>{opt.value}</span>
                        </Badge>
                      ) : (
                        opt.value
                      )
                    return { key: String(opt.id), label }
                  })}
                />
              </>
            )}
            <Search
              label=""
              query={query}
              searchKey="keyword"
              action={handleFilterChange}
              modalProps={{
                placeholder: '姓名、房号、手机号',
                size: 'small',
                style: { width: '100%' }
              }}
            />
            {this.embedNewOrdersMobile && isUnassignedTab && (
              <p className="sider-embed-hint" role="note">
                点选一条安排，从底部打开排期；可点「返回」或灰幕关闭
              </p>
            )}
            <div className="sider-count">共 {totalRow} {isUnassignedTab ? '条' : '位客人'}</div>
          </div>
        )}
        <div className="sider-list">
          <Spin spinning={fetching}>
            {records.length === 0 ? (
              <Empty
                description={this.state.siderCollapsed ? '暂无' : (fetching ? '加载中...' : (isUnassignedTab ? '暂无未分配安排' : '暂无订单'))}
              />
            ) : (
              <List
                dataSource={records}
                renderItem={(item) => {
                  const collapsed = this.state.siderCollapsed
                  if (isUnassignedTab) {
                    const isSelected = String(item.id) === String(selectedUnassignedPlanId)
                    const hasTech = item.technicianId != null && String(item.technicianId) !== '' && Number(item.technicianId) !== 0
                    const showException =
                      item.isExecute || hasTech
                    return (
                      <List.Item
                        className={`order-list-item order-list-item--unassigned ${isSelected ? 'selected' : ''} ${collapsed ? 'collapsed' : ''}`}
                        onClick={() => this.handleSelectUnassignedPlan(item)}
                      >
                        <div className="order-item-content unassigned-plan-item">
                          {collapsed ? (
                            <div
                              className="unassigned-plan-collapsed"
                              title={`${String(item.memberName ?? '')} ${String(item.projectName ?? '')}`.trim()}
                            >
                              <span className="unassigned-collapsed-name">{renderTextStr(item.memberName)}</span>
                            </div>
                          ) : (
                            <>
                              <div className="unassigned-plan-title-band">
                                <div className="unassigned-name-row">
                                  <strong className="unassigned-member-name">{renderTextStr(item.memberName)}</strong>
                                </div>
                                <div className="unassigned-name-divider" aria-hidden="true" />
                                <div className="unassigned-name-row unassigned-name-row--project">
                                  <strong className="unassigned-project-name">{renderTextStr(item.projectName)}</strong>
                                </div>
                                {showException && (
                                  <div className="unassigned-plan-exception">
                                    {item.isExecute && <Tag color="success">已执行</Tag>}
                                    {hasTech && <Tag>技师 {renderTextStr(item.technicianName)}</Tag>}
                                  </div>
                                )}
                              </div>
                              <div className="order-item-row sub unassigned-plan-meta">
                                房号 {renderTextStr(item.roomName)}
                              </div>
                              <div className="order-item-row sub unassigned-plan-meta">
                                预约 {formatUnassignedDaySec(item.appointmentDate)} · {formatUnassignedHmSec(item.startTime)} ～ {formatUnassignedHmSec(item.endTime)}
                              </div>
                            </>
                          )}
                        </div>
                      </List.Item>
                    )
                  }
                  const isSelected = String(item.id) === String(selectedOrderId)
                  const momAppointInTime = item.momAppointInTime
                  const momCheckInTime = item.momCheckInTime
                  const hasCheckInTime = item.checkInStatus === '入住中' || item.checkInStatus === '已离院'
                  const appointInTimeToday = momAppointInTime && dayjs.unix(momAppointInTime).isSame(dayjs(), 'day')
                  const checkInTimeToday = hasCheckInTime && momCheckInTime && dayjs.unix(momCheckInTime).isSame(dayjs(), 'day')
                  const isCheckedInTab = String(query?.checkInStatus ?? 2) === '2'
                  let checkInTodayTagText = null
                  if (!collapsed) {
                    if (hasCheckInTime && checkInTimeToday && isCheckedInTab) {
                      checkInTodayTagText = '当天已入住'
                    } else if (!hasCheckInTime && appointInTimeToday && !isCheckedInTab) {
                      checkInTodayTagText = '预计当天入住'
                    }
                  }
                  return (
                    <List.Item
                      className={`order-list-item ${isSelected ? 'selected' : ''} ${collapsed ? 'collapsed' : ''}`}
                      onClick={() => this.handleSelectOrder(item)}
                    >
                      <div className="order-item-content">
                        <div className="order-item-row customer-row">
                          <span className="customer-name">{renderTextStr(item.customerName)}</span>
                          {checkInTodayTagText && (
                            <Tag color="orange" className="check-in-today-tag" icon={<CalendarOutlined />}>
                              {checkInTodayTagText}
                            </Tag>
                          )}
                          {!collapsed && (
                            <Permission rootPath="dispatchPage">
                              <span pathitem={['dispatch', 'assignTechnician']} checkarray={['buttonCodes']}>
                                <DispatcherTechnicianModal
                                  title={`为 ${renderTextStr(item.customerName)} 安排专属技师`}
                                  orderId={item.id}
                                  baseProps={{
                                    storeId: query?.storeId,
                                    technicianValue: item.technicians,
                                    nurseValue: item.nurses,
                                  }}
                                  onSuccess={() => this.handleModalSuccess(item)}
                                >
                                  <Button
                                    type="link"
                                    size="small"
                                    className="assign-technician-btn"
                                  >
                                    为她安排专属技师
                                  </Button>
                                </DispatcherTechnicianModal>
                              </span>
                            </Permission>
                          )}
                        </div>
                        {!collapsed && (
                          <>
                            <div className="order-item-row sub">
                              房号：{renderTextStr(item.roomName)} | 套餐：{renderTextStr(item.packageName)}
                            </div>
                            {item.startTime && (
                              <div className="order-item-row sub">
                                服务期限：{formatDateTimeStr(item.startTime)} ~ {formatDateTimeStr(item.endTime)}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </List.Item>
                  )
                }}
              />
            )}
          </Spin>
          {!this.state.siderCollapsed && !isUnassignedTab && totalRow > pageSize && (
            <div className="sider-pagination">
              <Button
                size="small"
                disabled={pageNumber <= 1}
                onClick={() => fetchOrders({ ...query, pageNumber: pageNumber - 1 })}
              >
                上一页
              </Button>
              <span className="page-info">
                {pageNumber} / {Math.ceil(totalRow / pageSize)}
              </span>
              <Button
                size="small"
                disabled={pageNumber >= Math.ceil(totalRow / pageSize)}
                onClick={() => fetchOrders({ ...query, pageNumber: pageNumber + 1 })}
              >
                下一页
              </Button>
            </div>
          )}
        </div>
      </Sider>
    )
  }

  closeEmbedMobileUnassignedDetail = () => {
    this.setState({ selectedUnassignedPlanId: null })
  }

  /** 新订单：详情 + 排期块（与右栏/抽屉复用） */
  renderUnassignedOrderDetailBlocks = (plan, unassignedOrderQuery, orderDetail, { descColumns } = {}) => {
    const { refreshList, embedUrlStoreId } = this.props
    const qStore = unassignedOrderQuery?.storeId
    const resolvedStoreId =
      qStore != null && String(qStore).trim() !== ''
        ? String(qStore).trim()
        : (embedUrlStoreId != null && String(embedUrlStoreId).trim() !== ''
            ? String(embedUrlStoreId).trim()
            : undefined)
    const hasTech = plan.technicianId != null && String(plan.technicianId) !== '' && Number(plan.technicianId) !== 0
    const planOrderTotalId = resolveOrderTotalIdFromUnassignedPlan(plan)
    const profileTechnicians =
      planOrderTotalId != null &&
      orderDetail &&
      String(orderDetail.id ?? '') === String(planOrderTotalId)
        ? orderDetail.technicians
        : undefined
    const unassignedApiTechnicians = Array.isArray(plan.technicians) ? plan.technicians : null
    const tagTechniciansForPanel =
      Array.isArray(profileTechnicians) && profileTechnicians.length > 0
        ? profileTechnicians
        : (unassignedApiTechnicians && unassignedApiTechnicians.length > 0
            ? unassignedApiTechnicians
            : undefined)
    const col = descColumns != null ? descColumns : (this.state.isMobileLayout ? 1 : 2)
    return (
      <>
        <Card title="未分配产康安排" size="small" className="detail-card unassigned-plan-detail-card">
          <div className="unassigned-plan-detail-hero">
            <div className="unassigned-name-row">
              <span className="unassigned-field-label">客户: </span>
              <Text strong className="unassigned-detail-member-name">
                {renderTextStr(plan.memberName)}
              </Text>
            </div>
            <div className="unassigned-name-divider" aria-hidden="true" />
            <div className="unassigned-name-row unassigned-name-row--project">
              <span className="unassigned-field-label">项目: </span>
              <Text strong className="unassigned-detail-project-name">
                {renderTextStr(plan.projectName)}
              </Text>
            </div>
          </div>
          <Descriptions
            bordered
            column={col}
            size="small"
            className="unassigned-plan-detail-desc"
            style={{ marginTop: 16 }}
          >
            <Descriptions.Item label="房间">{renderTextStr(plan.roomName)}</Descriptions.Item>
            <Descriptions.Item label="预约日">{formatUnassignedDaySec(plan.appointmentDate)}</Descriptions.Item>
            {/* column=1（手机）时每行 span 之和须为 1，不能用 span={2} */}
            <Descriptions.Item label="时段" span={col >= 2 ? 2 : 1}>
              {formatUnassignedHmSec(plan.startTime)} ~ {formatUnassignedHmSec(plan.endTime)}
            </Descriptions.Item>
            <Descriptions.Item label="技师">
              {hasTech ? renderTextStr(plan.technicianName) : '（待分配）'}
            </Descriptions.Item>
            <Descriptions.Item label="是否已执行">
              {plan.isExecute ? '是' : '否'}
            </Descriptions.Item>
          </Descriptions>
        </Card>
        <Card title="产康师与排期" size="small" className="detail-card" style={{ marginTop: 16 }}>
          <UnassignedProjectPlanBookPanel
            key={String(plan.id)}
            plan={plan}
            storeId={resolvedStoreId}
            technicianValue={tagTechniciansForPanel}
            useMobileDatePicker={this.embedNewOrdersMobile}
            mobileUaDevice={this.props.embedMobileUaDevice}
            onSuccess={() => {
              if (typeof refreshList === 'function') refreshList()
              this.loadProjectPlanUnassignedCount()
              if (this.embedNewOrdersMobile) {
                this.closeEmbedMobileUnassignedDetail()
              }
            }}
          />
        </Card>
      </>
    )
  }

  /**
   * 新订单：ProjectPlanVO 无 order-total id，不拉订单详情，仅展示选中安排
   */
  renderUnassignedOrderRightPanel = () => {
    const { orderObj } = this.props
    const { detail: { data: orderDetail } = {} } = orderObj || {}
    const { isMobileLayout, selectedUnassignedPlanId } = this.state
    const { query: unassignedOrderQuery = {} } = orderObj?.dispatchOrderList || {}
    const { dispatchOrderList: { data: listData = {} } = {} } = orderObj || {}
    const { records: listRecords = [] } = listData
    const plan = listRecords.find((r) => String(r.id) === String(selectedUnassignedPlanId))

    if (!selectedUnassignedPlanId) {
      return (
        <Content className="dispatch-content">
          <div className="right-empty">
            <Empty
              description={
                isMobileLayout ? '请从上方列表选择一条未分配安排' : '请从左侧列表选择一条未分配安排'
              }
            />
          </div>
        </Content>
      )
    }
    if (!plan) {
      return (
        <Content className="dispatch-content">
          <div className="right-empty">
            <Empty description="记录已更新，请重新选择" />
          </div>
        </Content>
      )
    }
    return (
      <Content className="dispatch-content">
        {this.renderUnassignedOrderDetailBlocks(plan, unassignedOrderQuery, orderDetail, {})}
      </Content>
    )
  }

  /** 新订单·手机仅嵌入：底部抽屉内详情（不重复 renderUnassignedOrderRightPanel 的 Empty 逻辑，由 open 与 plan 控制） */
  renderEmbedNewOrdersMobileUnassignedDrawer = () => {
    if (!this.embedNewOrdersMobile) return null
    const { orderObj } = this.props
    const { selectedUnassignedPlanId } = this.state
    const { query: unassignedOrderQuery = {} } = orderObj?.dispatchOrderList || {}
    const { dispatchOrderList: { data: listData = {} } = {} } = orderObj || {}
    const { detail: { data: orderDetail } = {} } = orderObj || {}
    const { records: listRecords = [] } = listData
    const plan = listRecords.find((r) => String(r.id) === String(selectedUnassignedPlanId))
    const open = selectedUnassignedPlanId != null
    const titleText = plan
      ? `${renderTextStr(plan.memberName)} · ${renderTextStr(plan.projectName)}`
      : '产康排期'
    return (
      <Drawer
        className="dispatch-embed-new-orders-drawer"
        title={(
          <div className="dispatch-embed-new-orders-drawer__title">
            <Button
              type="text"
              size="small"
              icon={<LeftOutlined />}
              onClick={this.closeEmbedMobileUnassignedDetail}
            >
              返回
            </Button>
            <Text ellipsis className="dispatch-embed-new-orders-drawer__title-text">
              {titleText}
            </Text>
          </div>
        )}
        closable={false}
        placement="bottom"
        height="92%"
        open={open}
        onClose={this.closeEmbedMobileUnassignedDetail}
        destroyOnClose
        push={false}
        styles={{ body: { padding: '12px 12px 24px' } }}
      >
        {open && !plan && (
          <div className="right-empty" style={{ padding: '32px 0' }}>
            <Empty description="记录已更新，请重新选择" />
          </div>
        )}
        {open && plan && (
          <div className="dispatch-embed-new-orders-drawer__body">
            {this.renderUnassignedOrderDetailBlocks(plan, unassignedOrderQuery, orderDetail, { descColumns: 1 })}
          </div>
        )}
      </Drawer>
    )
  }

  renderRightContent = () => {
    const { orderObj, dispatchPage, dispatch } = this.props
    const {
      maternityCenterChanKangProjects: { data: maternityChanKangData, fetching: chanKangFetching },
      detail: { data: orderDetail, fetching: profileFetching },
      dispatchOrderList: { query: orderQuery },
      maternityCenterOrderProjectArray: { type1, type2, type3 }
    } = orderObj
    if (String(orderQuery?.checkInStatus ?? 2) === '1') {
      if (this.embedNewOrdersMobile) {
        return null
      }
      return this.renderUnassignedOrderRightPanel()
    }
    const orderProjectList1 = Array.isArray(type1) ? type1 : []
    const orderProjectList2 = Array.isArray(type2) ? type2 : []
    const orderProjectList3 = Array.isArray(type3) ? type3 : []
    const { selectedOrderId } = dispatchPage
    const rawPackageItems = [...(maternityChanKangData || [])].sort((a, b) =>
      (a?.projectName ?? '').localeCompare(b?.projectName ?? '')
    )
    const statusCounts = { all: 0, WAIT_APPOINTMENT: 0, APPOINTED: 0, WAIT_EXECUTE: 0, EXECUTING: 0, COMPLETED: 0 }
    rawPackageItems.forEach((item) => {
      const s = this.getPackageItemExecStatus(item)
      if (statusCounts[s] !== undefined) statusCounts[s]++
      statusCounts.all++
    })
    const { projectNameKeyword, execStatusFilter } = this.state
    let filteredItems = rawPackageItems
    if (projectNameKeyword) {
      filteredItems = filteredItems.filter((r) => (r?.projectName ?? '').toLowerCase().includes(projectNameKeyword.toLowerCase()))
    }
    if (execStatusFilter !== 'all') {
      filteredItems = filteredItems.filter((r) => this.getPackageItemExecStatus(r) === execStatusFilter)
    }
    const packageItemsWithRowSpan = this.buildPackageItemsWithRowSpan(filteredItems)

    if (!selectedOrderId) {
      return (
        <Content className="dispatch-content">
          <div className="right-empty">
            <Empty
              description={
                this.state.isMobileLayout ? '请从上方列表选择客人' : '请从左侧选择客人'
              }
            />
          </div>
        </Content>
      )
    }

    const { isMobileLayout } = this.state

    const columns = [
      {
        title: '项目名称',
        dataIndex: 'projectName',
        key: 'projectName',
        width: 150,
        onCell: (record) => {
          const span = record._projectNameRowSpan ?? 1
          return { rowSpan: span }
        },
        render: (t, record) => {
          const span = record._projectNameRowSpan ?? 1
          if (span === 0) return null
          return <span className="package-project-name-cell">{renderTextStr(t)}</span>
        }
      },
      {
        title: '购买/可用',
        key: 'remaining',
        width: 88,
        render: (_, r) => (
          <span>
            {renderCountOrGrayZero(r.num)}
            {' / '}
            {renderCountOrGrayZero(r.useNum)}
          </span>
        )
      },
      {
        title: '项目类型',
        dataIndex: 'typeName',
        key: 'typeName',
        width: 118,
        render: (t, record) => {
          const typeText = renderTextStr(t)
          if (t !== '月子套餐') return typeText
          const deadline = record.deadline
          const deadlineText = formatDayString(deadline)
          if (!deadlineText) return typeText
          const due = typeof deadline === 'number' && String(Math.abs(deadline)).length <= 10
            ? dayjs.unix(deadline)
            : dayjs(deadline)
          const warnFrom = due.subtract(1, 'month').startOf('day')
          const isDeadlineSoon = due.isValid() && !dayjs().startOf('day').isBefore(warnFrom)
          return (
            <div className="package-type-cell">
              <div>{typeText}</div>
              <Text
                type={isDeadlineSoon ? 'danger' : 'secondary'}
                className={isDeadlineSoon ? 'package-type-deadline package-type-deadline--soon' : 'package-type-deadline'}
              >
                截止 {deadlineText}
              </Text>
            </div>
          )
        }
      },
      {
        title: '执行状态',
        dataIndex: 'executeStatus',
        key: 'executeStatus',
        width: 100,
        render: (_, record) => {
          const status = this.getPackageItemExecStatus(record)
          const config = ITEM_EXEC_STATUS[status]
          return (
            <span>
              {status === 'WAIT_APPOINTMENT' && <WarningOutlined style={{ color: '#faad14', marginRight: 4 }} />}
              <Tag color={config?.color}>{config?.text ?? status}</Tag>
            </span>
          )
        }
      },
      {
        title: '最新预约时间',
        key: 'appointmentTime',
        width: 280,
        render: (_, r) => {
          const start = r.latestAppointmentStartTime
          const end = r.latestAppointmentEndTime
          if (!start && !end) return '-'
          return `${start ? formatDateTimeStr(start) : '-'} ~ ${end ? dayjs.unix(end).format('HH:mm') : '-'}`
        }
      },
      {
        title: '执行人',
        dataIndex: 'executor',
        key: 'executor',
        width: 100,
        render: (t) => renderTextStr(t)
      },
      {
        title: '操作',
        key: 'action',
        width: 80,
        fixed: 'right',
        render: (_, record) => (
            <span onClick={(e) => e.stopPropagation()}>
              <Permission
                rootPath="dispatchPage"
                dataRes={[record.executeStatus]}
                dataRes2={[String(orderQuery?.checkInStatus ?? '')]}
              >
                <span pathitem={['dispatch', 'a']} checkarray={['dataRes', 'dataRes2', 'buttonCodes']}>
                  <SingleProjectAppointmentModal
                    orderId={selectedOrderId}
                    title={`预约：${renderTextStr(record.projectName)} · 服务时长：${Number.isFinite(Number(record.serviceTime)) && Number(record.serviceTime) > 0 ? `${Number(record.serviceTime)} 分钟` : '—'}`}
                    packageItemId={record.id}
                    projectId={record.projectId}
                    projectName={record.projectName}
                    serviceTime={Number(record.serviceTime)}
                    baseProps={{
                      // 列表 query 在部分时机可能未带 storeId；详情接口通常有门店，避免预约弹窗不拉技师
                      storeId: orderQuery?.storeId ?? orderDetail?.storeId,
                      technicianValue: orderDetail?.technicians,
                      nurseValue: orderDetail?.nurses,
                    }}
                    onSuccess={() => this.handleModalSuccess(selectedOrderId)}
                  >
                    <Button type="link" size="small" >
                      预约
                    </Button>
                  </SingleProjectAppointmentModal>
                </span>
                <span pathitem={['dispatch', 'b']} checkarray={['dataRes', 'buttonCodes']}>
                  <CancelMemberProjectPlansModal
                    memberProjectId={record.id}
                    projectName={record.projectName}
                    dispatch={dispatch}
                    onSuccess={() => this.handleModalSuccess(selectedOrderId)}
                  >
                    <Button type="link" size="small" danger>
                      取消预约
                    </Button>
                  </CancelMemberProjectPlansModal>
                </span>
              </Permission>
            </span>
        )
      },
    ]

    const roomPlans = orderDetail?.roomPlans || []
    return (
      <Content className="dispatch-content">
        <Spin spinning={profileFetching}>
          <Card title="套餐详情" size="small" className="detail-card">
            {orderDetail ? (
              <>
              <Descriptions bordered column={isMobileLayout ? 1 : 2} size="small">
                <Descriptions.Item label="客户名称">{renderTextStr(orderDetail.customerName || orderDetail.maternalInfo?.name)}</Descriptions.Item>
                <Descriptions.Item label="套餐名称">{renderTextStr(orderDetail.packageName)}</Descriptions.Item>
                <Descriptions.Item label="套餐类型">{renderTextStr(orderDetail.orderTypeText)}</Descriptions.Item>
                <Descriptions.Item label="服务起止与天数">{this.renderServicePeriodSummary(orderDetail)}</Descriptions.Item>
                <Descriptions.Item label="责任产康师">{renderTextStr(this.formatPrincipalNames(orderDetail.technicians))}</Descriptions.Item>
                <Descriptions.Item label="责任护士">{renderTextStr(this.formatPrincipalNames(orderDetail.nurses))}</Descriptions.Item>
              </Descriptions>
              {Array.isArray(orderDetail.continuedOrderTotals) && orderDetail.continuedOrderTotals.length > 0 ? (
                <div style={{ marginTop: 16 }}>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>续住信息</Text>
                  <Table
                    size="small"
                    pagination={false}
                    dataSource={orderDetail.continuedOrderTotals}
                    rowKey={(r) => (r.id != null ? String(r.id) : `${r.contractNum ?? ''}-${r.signTime ?? ''}`)}
                    scroll={{ x: 916 }}
                    columns={[
                      { title: '房型', dataIndex: 'roomTypeName', key: 'roomTypeName', width: 100, render: (t) => renderTextStr(t) },
                      { title: '服务天数', dataIndex: 'serviceDay', key: 'serviceDay', width: 88, render: (t) => renderTextStr(t) },
                      { title: '签约时间', dataIndex: 'signTime', key: 'signTime', width: 120, render: (t) => formatDayString(t) },
                      {
                        title: '备注',
                        dataIndex: 'remark',
                        key: 'remark',
                        width: 200,
                        ellipsis: true,
                        render: (t) => renderTextStr(t)
                      }
                    ]}
                  />
                </div>
              ) : null}
              </>
            ) : (
              <Empty description="加载中..." />
            )}
          </Card>
          <Card
            title="入住房间"
            size="small"
            className="detail-card"
            extra={
              roomPlans.length > 0 ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  共 {roomPlans.length} 条
                </Text>
              ) : null
            }
          >
            {roomPlans.length > 0 ? (
              this.renderRoomBookingList(roomPlans)
            ) : orderDetail ? (
              <div className="compact-empty">
                <Empty description="暂无房间预约记录" />
              </div>
            ) : null}
          </Card>
        </Spin>
        <Permission rootPath="dispatchPage">
          <div pathitem={['dispatch', 'chanKang']} checkarray={['moduleCodes']}>
            <Card
              title="产康项目"
              size="small"
              className="detail-card"
              extra={rawPackageItems.length > 0 ? <span className="card-extra-count">共 {rawPackageItems.length} 项</span> : null}
            >
              <Spin spinning={chanKangFetching}>
                {rawPackageItems.length > 0 && (
                  <div className="package-filter-bar">
                    <div className="status-filter-group">
                      {STATUS_FILTER_OPTIONS.map((f) => (
                        <Tag
                          key={f.key}
                          color={execStatusFilter === f.key ? f.color : undefined}
                          className={`status-filter-tag ${execStatusFilter === f.key ? 'active' : ''}`}
                          onClick={() => this.setState({ execStatusFilter: f.key })}
                        >
                          {f.label} ({statusCounts[f.key]})
                        </Tag>
                      ))}
                    </div>
                    <Search
                      query={{ projectNameKeyword }}
                      searchKey="projectNameKeyword"
                      actionChange={(q) => this.setState({
                        projectNameKeyword: q.projectNameKeyword ?? ''
                      })}
                      modalProps={{
                        placeholder: '搜索项目名称',
                        style: isMobileLayout ? { width: '100%', maxWidth: 'none' } : { maxWidth: 200 },
                        size: 'small'
                      }}
                    />
                  </div>
                )}
                {packageItemsWithRowSpan.length === 0 ? (
                  <Empty description={rawPackageItems.length === 0 ? '暂无套餐项目' : '暂无匹配的项目'} />
                ) : (
                  <Table
                    columns={columns}
                    dataSource={packageItemsWithRowSpan}
                    rowKey="id"
                    size="small"
                    pagination={false}
                    scroll={{ x: 720 }}
                    rowClassName={(record) => {
                      const status = this.getPackageItemExecStatus(record)
                      return `package-row-${status}`
                    }}
                  />
                )}
              </Spin>
            </Card>
          </div>
        </Permission>
        {orderProjectList1.length > 0 && (
          <Permission rootPath="dispatchPage">
            <div pathitem={['dispatch', 'orderProjectArray', 'type1']} checkarray={['moduleCodes']}>
              {this.renderOrderProjectArrayCard(orderProjectList1, 0)}
            </div>
          </Permission>
        )}
        {orderProjectList2.length > 0 && (
          <Permission rootPath="dispatchPage">
            <div pathitem={['dispatch', 'orderProjectArray', 'type2']} checkarray={['moduleCodes']}>
              {this.renderOrderProjectArrayCard(orderProjectList2, 1)}
            </div>
          </Permission>
        )}
        {orderProjectList3.length > 0 && (
          <Permission rootPath="dispatchPage">
            <div pathitem={['dispatch', 'orderProjectArray', 'type3']} checkarray={['moduleCodes']}>
              {this.renderOrderProjectArrayCard(orderProjectList3, 2)}
            </div>
          </Permission>
        )}
      </Content>
    )
  }

  render() {
    const { isMobileLayout } = this.state
    const isEmbed = this.embedNewOrdersMobile
    return (
      <Layout
        className={
          isMobileLayout
            ? `dispatch-layout dispatch-layout--mobile${
                isEmbed ? ' dispatch-layout--embed-new-orders' : ''
              }`
            : 'dispatch-layout'
        }
      >
        {this.renderLeftSider()}
        {this.renderRightContent()}
        {this.renderEmbedNewOrdersMobileUnassignedDrawer()}
      </Layout>
    )
  }
}

export default MaternityCenterDispatcherContent
